import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import dialogCss from './lib-dialog.css';

const openDialogs = new Set<LibDialog>();
let previousOverflow = '';

@customElement('lib-dialog')
export class LibDialog extends LibElement {
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(dialogCss)];

  @property({ type: Boolean }) visible = false;
  @property({ attribute: 'close-on-escape', converter: value => value !== 'false' }) closeOnEscape = true;
  @property({ type: Boolean, attribute: 'close-on-outside-click' }) closeOnOutsideClick = false;
  @property({ attribute: 'accessible-name' }) accessibleName = '';

  private get dialog() { return this.renderRoot.querySelector('dialog'); }

  override updated() {
    const dialog = this.dialog;
    if (!dialog) return;
    if (this.visible && !dialog.open) {
      dialog.showModal();
      if (openDialogs.size === 0) {
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
      openDialogs.add(this);
      this.emit('lib-dialog-shown');
    } else if (!this.visible && dialog.open) {
      this.close();
    }
  }

  close() {
    if (!this.dialog?.open) return;
    this.visible = false;
    this.dialog.close();
    this.releaseScroll();
  }

  private releaseScroll() {
    if (openDialogs.delete(this) && openDialogs.size === 0) {
      document.body.style.overflow = previousOverflow;
    }
  }

  override disconnectedCallback() {
    unbindDialog(this);
    this.close();
    super.disconnectedCallback();
  }

  private onBackdrop(event: MouseEvent) {
    const dialog = this.dialog;
    if (!dialog || event.target !== dialog || !this.closeOnOutsideClick) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom) this.close();
  }

  override render() {
    return html`<dialog aria-label=${this.accessibleName || this.querySelector('.lib-dialog-header')?.textContent?.trim() || nothing}
      @cancel=${(event: Event) => { event.preventDefault(); if (this.closeOnEscape) this.close(); }}
      @click=${this.onBackdrop}
      @close=${() => { this.releaseScroll(); this.emit('lib-dialog-closed'); }}><slot></slot></dialog>`;
  }
}

interface DialogReference { invokeMethodAsync(method: string): Promise<unknown>; }
const bindings = new WeakMap<LibDialog, { shown: () => void; closed: () => void }>();

export async function bindDialog(element: LibDialog | null, reference: DialogReference) {
  if (!element) return;
  await element.updateComplete;
  const shown = () => { void reference.invokeMethodAsync('OnDialogShown'); };
  const closed = () => { void reference.invokeMethodAsync('OnDialogClosed'); };
  bindings.set(element, { shown, closed });
  element.addEventListener('lib-dialog-shown', shown);
  element.addEventListener('lib-dialog-closed', closed);
  if (element.shadowRoot?.querySelector('dialog')?.open) shown();
}

export function closeDialog(element: LibDialog | null) { element?.close(); }

export function unbindDialog(element: LibDialog | null) {
  if (!element) return;
  const binding = bindings.get(element);
  if (binding) {
    element.removeEventListener('lib-dialog-shown', binding.shown);
    element.removeEventListener('lib-dialog-closed', binding.closed);
    bindings.delete(element);
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-dialog': LibDialog; }
}
